import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Chip, Skeleton, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions, Button
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import { HotelService } from '../api/services/hotelService';

export default function HotelAuditLogsModal({ open, onClose, tenantId, setNotification }) {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchLogs = useCallback(async () => {
        if (!tenantId) return;
        setLoading(true);
        setError(null);
        try {
            const response = await HotelService.getAuditLogs(tenantId, tenantId);
            setLogs(response.data || response || []);
        } catch (err) {
            console.error('Failed to fetch audit logs:', err);
            setError('Could not load activity history.');
            setNotification({ open: true, message: err?.response?.data?.message || err?.message || 'Failed to load audit logs', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [tenantId, setNotification]);

    useEffect(() => {
        if (open && tenantId) {
            fetchLogs();
        } else {
            setLogs([]);
        }
    }, [open, tenantId, fetchLogs]);

    const getStatusColor = (status) => {
        const lower = String(status || '').toLowerCase();
        if (lower === 'success') return 'success';
        if (lower === 'error' || lower === 'failure' || lower === 'failed') return 'error';
        if (lower === 'pending') return 'warning';
        return 'default';
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3, padding: 1, backgroundColor: '#fafafa' } }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HistoryIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold">Activity History</Typography>
                </Box>
            </DialogTitle>
            <DialogContent dividers sx={{ backgroundColor: 'white' }}>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Historical stream of audit logs for this property.
                    </Typography>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Timestamp</TableCell>
                                <TableCell>User / Actor</TableCell>
                                <TableCell>Action Performed</TableCell>
                                <TableCell>Module / Target</TableCell>
                                <TableCell>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                Array.from(new Array(5)).map((_, index) => (
                                    <TableRow key={`skeleton-${index}`}>
                                        <TableCell><Skeleton variant="text" width={140} /></TableCell>
                                        <TableCell><Skeleton variant="text" width={160} /></TableCell>
                                        <TableCell><Skeleton variant="text" width={180} /></TableCell>
                                        <TableCell><Skeleton variant="text" width={120} /></TableCell>
                                        <TableCell><Skeleton variant="rounded" width={80} height={24} /></TableCell>
                                    </TableRow>
                                ))
                            ) : logs.length > 0 ? (
                                logs.map((log) => (
                                    <TableRow key={log.id || Math.random()} hover>
                                        <TableCell>
                                            {log.createdAt ? new Date(log.createdAt).toLocaleString() : (log.timestamp ? new Date(log.timestamp).toLocaleString() : 'N/A')}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="medium">
                                                {log.performedBy?.name || log.user?.name || log.userName || log.actor || 'System'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {log.performedBy?.email || log.user?.email || log.userEmail || ''}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{log.action || 'Unknown Action'}</TableCell>
                                        <TableCell>
                                            {log.entityName || log.module || log.target || 'System'}
                                            {log.changedFields && log.changedFields.length > 0 && (
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                    ({log.changedFields.join(', ')})
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={log.status || 'Success'} 
                                                size="small" 
                                                color={getStatusColor(log.status || 'Success')} 
                                                sx={{ fontWeight: 'bold' }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                                        <Typography variant="body1" color="text.secondary">
                                            No audit logs available for this property.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions sx={{ padding: '16px 24px' }}>
                <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}
